import React from 'react'
import { AuthorizeSkinAgreement } from '../../types'
import styles from './application-flow-product-retention-dialog.module.scss'

interface Props {

  /** 展示的大字金额，如 '100,000' 或 '200,000' */
  amount?: string

  /** 协议列表，用于渲染提示中各协议链接 */
  agreements?: readonly AuthorizeSkinAgreement[]

  /** 对客品牌名称，默认为 '聚融易' */
  brandName?: string

  /** ✕ 关闭按钮是否在延时后允许点击 */
  canClose: boolean

  /** 与页面底部领取按钮共用的倒计时文案。 */
  countdownText: string

  /** 提交按钮的前缀文案，默认为 '立即提交' */
  submitLabelPrefix?: string

  /** 挽留弹窗的主题插图地址 */
  illustrationSrc: string

  /** 点击 ✕ 关闭按钮：仅关闭挽留弹窗，留在当前页 */
  onDismiss: () => void

  /** 点击协议名称时触发 */
  onOpenAgreement?: (agreementKey: string) => void

  /** 点击 立即提交 按钮：关闭挽留弹窗并触发业务提交动作 */
  onSubmit: () => void

  /** 提交请求进行中的 loading 状态 */
  submitting: boolean

  /** 挽留弹窗显隐状态 */
  visible: boolean
}

/**
 * ApplicationFlowProductRetentionDialog — 申请流程新皮肤产品/授权页挽留弹窗 UI 组件
 * @returns {React.ReactElement | null} 挽留弹窗节点
 */
const ApplicationFlowProductRetentionDialog: React.FC<Props> = ({
  agreements,
  amount = '100,000',
  brandName = '聚融易',
  canClose,
  countdownText,
  illustrationSrc,
  onDismiss,
  onOpenAgreement,
  onSubmit,
  submitLabelPrefix = '立即提交',
  submitting,
  visible,
}) => {
  if (!visible) {
    return null
  }

  const hasAgreements = Array.isArray(agreements) && agreements.length > 0

  return (
    <div aria-label="产品页挽留" aria-modal="true" className={styles.mask} role="dialog">
      <section className={styles.card}>
        {canClose ? (
          <button aria-label="关闭挽留弹窗" className={styles.close} onClick={onDismiss} type="button">
            ✕
          </button>
        ) : null}
        <img alt="" className={styles.illustration} height="94" src={illustrationSrc} width="130" />
        <h2 className={styles.title}>
          额度即将失效，<br />
          请<span className={styles.highlight}>立即提现</span>
        </h2>
        <div className={styles.amountCard}>
          <small className={styles.amountLabel}>最高可借</small>
          <strong className={styles.amountValue}>¥{amount}</strong>
        </div>
        <button className={styles.submit} disabled={submitting} onClick={onSubmit} type="button">
          {submitting ? (
            '提交中...'
          ) : (
            <>
              <span>{submitLabelPrefix}</span>
              {countdownText ? <span className={styles.countdown}>{countdownText}</span> : null}
            </>
          )}
        </button>
        <p className={styles.notice}>
          您点击“立即提交”，{brandName}即视您已阅读同意
          {hasAgreements ? (
            agreements.map(agreement => (
              <button
                className={styles.protocolLink}
                key={agreement.key}
                onClick={() => onOpenAgreement?.(agreement.key)}
                type="button"
              >
                《{agreement.name}》
              </button>
            ))
          ) : null}
          的全部条款
        </p>
      </section>
    </div>
  )
}

export default React.memo(ApplicationFlowProductRetentionDialog)
